/**
 * ActionCard - Reusable card for displaying action items
 * Used in priority actions, alerts, and recommendations
 */

import React from 'react';
import { AlertTriangle, TrendingUp, Zap, ChevronRight, Clock, CheckCircle } from 'lucide-react';

export interface ActionCardProps {
  type: 'urgent' | 'performance' | 'opportunity' | 'task' | 'alert';
  severity?: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  value?: string | number;
  deadline?: string;
  status?: 'pending' | 'in-progress' | 'completed';
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

const ActionCard: React.FC<ActionCardProps> = ({
  type,
  severity = 'medium',
  title,
  description,
  value,
  deadline,
  status,
  actionLabel,
  onAction,
  icon,
  className = ''
}) => {
  // Type configurations
  const typeConfig = {
    urgent: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-900',
      iconColor: 'text-red-600',
      defaultIcon: AlertTriangle
    },
    performance: {
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-900',
      iconColor: 'text-yellow-600',
      defaultIcon: TrendingUp
    },
    opportunity: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-900',
      iconColor: 'text-green-600',
      defaultIcon: Zap
    },
    task: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-900',
      iconColor: 'text-blue-600',
      defaultIcon: CheckCircle
    },
    alert: {
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-900',
      iconColor: 'text-orange-600',
      defaultIcon: AlertTriangle
    }
  };

  const config = typeConfig[type];
  const Icon = icon ? () => icon : config.defaultIcon;

  // Severity badge
  const severityBadge = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800'
  };

  // Status badge
  const statusBadge = {
    pending: 'bg-gray-100 text-gray-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800'
  };

  return (
    <div 
      className={`
        ${config.bgColor} 
        ${config.borderColor} 
        border rounded-lg p-4 
        hover:shadow-md transition-shadow 
        ${className}
      `}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <h4 className={`font-semibold ${config.textColor}`}>
              {title}
            </h4>
            {(severity || status) && (
              <div className="flex gap-2 mt-1">
                {severity && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityBadge[severity]}`}>
                    {severity.toUpperCase()}
                  </span>
                )}
                {status && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge[status]}`}>
                    {status.replace('-', ' ').toUpperCase()}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {value && (
          <span className={`text-lg font-bold ${config.textColor}`}>
            {value}
          </span>
        )}
      </div>
      
      <p className="text-sm text-gray-700 mb-3 ml-8">
        {description}
      </p>
      
      <div className="flex items-center justify-between ml-8">
        <div className="flex items-center gap-4">
          {deadline && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              {deadline}
            </div>
          )}
        </div>
        
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className={`
              flex items-center gap-1 
              px-3 py-1.5 
              text-xs font-medium 
              ${config.textColor} 
              bg-white 
              border ${config.borderColor} 
              rounded-md 
              hover:bg-gray-50 
              transition-colors
            `}
          >
            {actionLabel}
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ActionCard;